import { useCustom, useCustomMutation, useGetIdentity, useNavigation } from "@refinedev/core";
import { useParams } from "react-router-dom";
import { Edit } from "@refinedev/antd";
import { Form, Input, InputNumber, Spin, Typography, message } from "antd";
import { useState } from "react";
import { apiUrl } from "@/consts";

type ClubDetail = {
  club: {
    id: string;
    name: string;
    slug: string;
    description: string | null;
    contactPerson: string | null;
    contactEmail: string | null;
    contactPhone: string | null;
    seasonFeePercent: string | null;
    lottoCommissionPercent: string | null;
  };
  lotto?: {
    effectivePercent: number;
    globalPercent: number;
  };
};

export function ClubsEdit() {
  const { id } = useParams<{ id: string }>();
  const { show } = useNavigation();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const { mutate } = useCustomMutation();
  const { data: identity, isLoading: identityLoading } = useGetIdentity<{ role?: string }>();
  const isSuperAdmin = identity?.role === "SUPER_ADMIN";

  const { data, isLoading } = useCustom<ClubDetail>({
    url: `${apiUrl}/clubs/${id}`,
    method: "get",
    queryOptions: { enabled: !!id },
  });

  const club = data?.data?.club;
  const globalLottoPercent = data?.data?.lotto?.globalPercent;

  function handleFinish(values: any) {
    setSaving(true);
    const payload: Record<string, unknown> = {
      name: values.name,
      contactPerson: values.contactPerson || null,
      contactEmail: values.contactEmail || null,
      contactPhone: values.contactPhone || null,
      description: values.description || null,
      seasonFeePercent:
        values.seasonFeePercent === undefined || values.seasonFeePercent === null
          ? null
          : Number(values.seasonFeePercent),
    };
    // Only super-admins see/edit the lotto override field; omit the key otherwise
    // so a regular edit never clears an existing override.
    if (isSuperAdmin) {
      payload.lottoCommissionPercent =
        values.lottoCommissionPercent === undefined ||
        values.lottoCommissionPercent === null
          ? null
          : Number(values.lottoCommissionPercent);
    }
    mutate(
      {
        url: `${apiUrl}/clubs/${id}`,
        method: "patch",
        values: payload,
      },
      {
        onSuccess: () => {
          setSaving(false);
          message.success("Club updated");
          if (id) show("clubs", id);
        },
        onError: (err: any) => {
          setSaving(false);
          message.error(err?.response?.data?.error ?? err?.message ?? "Failed to update club");
        },
      }
    );
  }

  // Wait for identity too: isSuperAdmin gates the lotto override field, and
  // evaluating it as false during the identity-loading window would render the
  // form without that field (and omit it on an early submit).
  if (isLoading || identityLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!club) {
    return <Typography.Text type="danger">Club not found.</Typography.Text>;
  }

  return (
    <Edit
      title="Edit club"
      saveButtonProps={{ loading: saving, onClick: () => form.submit() }}
      canDelete={false}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{
          name: club.name,
          contactPerson: club.contactPerson ?? "",
          contactEmail: club.contactEmail ?? "",
          contactPhone: club.contactPhone ?? "",
          description: club.description ?? "",
          seasonFeePercent:
            club.seasonFeePercent != null ? Number(club.seasonFeePercent) : null,
          lottoCommissionPercent:
            club.lottoCommissionPercent != null
              ? Number(club.lottoCommissionPercent)
              : null,
        }}
      >
        <Form.Item
          label="Club name"
          name="name"
          rules={[{ required: true, message: "Name is required" }]}
        >
          <Input />
        </Form.Item>

        <Form.Item label="Contact person" name="contactPerson">
          <Input />
        </Form.Item>

        <Form.Item
          label="Contact email"
          name="contactEmail"
          rules={[{ type: "email", message: "Please enter a valid email address" }]}
        >
          <Input />
        </Form.Item>

        <Form.Item label="Contact phone" name="contactPhone">
          <Input />
        </Form.Item>

        <Form.Item label="Description" name="description">
          <Input.TextArea rows={3} />
        </Form.Item>

        <Form.Item
          label="Membership commission %"
          name="seasonFeePercent"
          extra="Our membership commission on each season invoice. Leave blank to use the platform default (3%)."
        >
          <InputNumber min={0} max={100} step={0.01} addonAfter="%" style={{ width: "100%" }} />
        </Form.Item>

        {isSuperAdmin && (
          <Form.Item
            label="Lotto commission %"
            name="lottoCommissionPercent"
            extra={
              globalLottoPercent != null
                ? `Per-club override of the platform lotto commission. Leave blank to use the platform default (currently ${globalLottoPercent}%).`
                : "Per-club override of the platform lotto commission. Leave blank to use the platform default."
            }
          >
            <InputNumber min={0} max={100} step={0.01} addonAfter="%" style={{ width: "100%" }} />
          </Form.Item>
        )}
      </Form>
    </Edit>
  );
}
